import React, {useState, useEffect, useCallback} from 'react';
import {v4 as uuidv4} from 'uuid';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';

import ExerciseAppBar from '../../components/ExerciseAppBar';
import ProductsOverlay from './ProductsOverlay';
import Board from './Board';
import SecondaryAppBar from '../../components/SecondaryAppBar';
import Card from '../../components/Card';

import FlamingoLiteracyLogoDark from '../../components/icons/FlamingoLiteracyLogoDark';

import baseStyles from '../../css/Base.module.css';
import '../css/app.css';

const defaultState = Object.freeze({
  columns: {
    1: {id: '1', sound: null, words: []},
    2: {id: '2', sound: null, words: []},
    3: {id: '3', sound: null, words: []},
    4: {id: '4', sound: null, words: []},
    5: {id: '5', sound: null, words: []},
    6: {id: '6', sound: null, words: []},
    7: {id: '7', sound: null, words: []},
  },
  columnOrder: ['1', '2', '3', '4', '5', '6', '7'],
});

export default function WordSortApp({exercise, handleSave, isEditable, mode}) {
  const [columns, setColumns] = useState(defaultState.columns);
  const [columnOrder, setColumnOrder] = useState(defaultState.columnOrder);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isErrorDialogVisible, setIsErrorDialogVisible] = useState(false);
  const [isCardVisible, setIsCardVisible] = useState(false);

  const loadStateFromConfig = useCallback(() => {
    const hasConfig = Object.keys(exercise?.config ?? {}).length > 0;

    if (hasConfig) {
      const config = JSON.parse(JSON.stringify(exercise.config));

      // If editing, ensure 7 columns visible
      if (isEditable && config.columnOrder.length < 7) {
        const numberColumnsToCreate = 7 - config.columnOrder.length;
        for (let i = 0; i < numberColumnsToCreate; i += 1) {
          const id = uuidv4();
          // TODO function to create new column
          const column = {
            id,
            sound: null,
            words: [],
          };
          config.columns[id] = column;
          config.columnOrder.push(id);
        }
      }

      setColumns(config.columns);
      setColumnOrder(config.columnOrder);
    }
  }, [exercise?.config, isEditable]);

  useEffect(() => {
    loadStateFromConfig();
  }, [exercise._id, loadStateFromConfig]);

  function handleClickSave() {
    // Validate that all columns (except word bank) containing words has a sound selected
    const workingColumns = Object.values(columns).filter((column) => column.id !== columnOrder[0]);
    const allColumnsWithWordsHasSound = workingColumns.every((column) => {
      if (column.words.length > 0) return !!column.sound;
      return true;
    });
    if (!allColumnsWithWordsHasSound) {
      setIsErrorDialogVisible(true);
      return;
    }

    handleSave(generateConfig());
  }

  function generateConfig() {
    const columnsClone = JSON.parse(JSON.stringify(columns));
    let columnOrderClone = JSON.parse(JSON.stringify(columnOrder));

    // Remove columns with no sound, no words
    const wordBankColumnId = columnOrder[0];
    Object.keys(columnsClone).forEach((columnId) => {
      const column = columnsClone[columnId];
      if (column.id !== wordBankColumnId && column.words.length === 0 && !column.sound) {
        delete columnsClone[columnId];
        columnOrderClone = columnOrderClone.filter((id) => id !== columnId);
      }
    });

    return {
      columns: columnsClone,
      columnOrder: columnOrderClone,
      isDrawerOpen,
      isErrorDialogVisible,
      isCardVisible,
    };
  }

  // Floating Action Button (+ symbol)
  function handleClickFAB() {
    setIsDrawerOpen(!isDrawerOpen);
  }

  function handleClickCloseDrawer() {
    setIsDrawerOpen(false);
  }

  function handleClickAddItem(id, model) {
    if (model.type === 'WORD') {
      const wordBankColumnId = columnOrder[0];
      const wordModel = {
        id,
        uuid: uuidv4(),
        word: model.word,
      };

      setColumns((prevColumns) => ({
        ...prevColumns,
        [wordBankColumnId]: {
          ...prevColumns[wordBankColumnId],
          words: [wordModel, ...prevColumns[wordBankColumnId].words],
        },
      }));
    }
  }

  // Move all chars back to char bank
  // When editing, also clears sounds
  function handleResetChars() {
    const wordBankKey = defaultState.columnOrder[0];
    let allWords = [];

    columnOrder.forEach((key) => {
      const column = columns[key];
      allWords = allWords.concat(column.words);
    });

    const columnsClone = JSON.parse(JSON.stringify(columns));
    Object.keys(columnsClone).forEach((key) => {
      columnsClone[key].words = [];
      if (isEditable) columnsClone[key].sound = null;
    });

    setColumns({
      ...columnsClone,
      [wordBankKey]: {
        ...defaultState.columns[wordBankKey],
        words: allWords,
      },
    });
    setIsCardVisible(false);
  }

  // Clear everything
  function handleStartOver() {
    setColumns(defaultState.columns);
    setColumnOrder(defaultState.columnOrder);
    setIsCardVisible(false);
    setIsDrawerOpen(false);
    setIsErrorDialogVisible(false);
  }

  function handleClickCard() {
    setIsCardVisible(!isCardVisible);
  }

  function handleOnChangeSound(columnId, sound) {
    const sourceColumn = columns[columnId];
    const newColumn = {
      ...sourceColumn,
      sound,
    };

    setColumns({
      ...columns,
      [columnId]: newColumn,
    });
  }

  function handleUpdateColumnOrder(columnOrder) {
    setColumnOrder(columnOrder);
  }

  function handleUpdateColumns(columns) {
    setColumns(columns);
  }

  function handleDeleteWord(column, wordIndex) {
    const columnWords = Array.from(column.words);
    columnWords.splice(wordIndex, 1); // remove word at index

    setColumns((prevColumns) => ({
      ...prevColumns,
      [column.id]: {
        ...prevColumns[column.id],
        words: columnWords,
      },
    }));
  }

  function renderErrorDialog() {
    return (
      <Dialog open={isErrorDialogVisible} onClose={() => setIsErrorDialogVisible(false)}>
        <DialogTitle>Error</DialogTitle>
        <DialogContent>
          <DialogContentText>A column containing words must have a sound selected.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsErrorDialogVisible(false)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      {mode === 'EXERCISE' && (
        <ExerciseAppBar exercise={exercise}>
          {isEditable && (
            <Button className={baseStyles['btn-pill']} color="inherit" onClick={handleClickSave}>
              SAVE THIS EXERCISE
            </Button>
          )}
        </ExerciseAppBar>
      )}

      <SecondaryAppBar
        onClickReset={handleResetChars}
        onClickClear={isEditable ? handleStartOver : null}
        onClickFAB={isEditable ? handleClickFAB : null}
        onClickCard={handleClickCard}
      />

      {renderErrorDialog()}

      <div className="screen-width-warning-cover">
        <FlamingoLiteracyLogoDark style={{width: 250, height: 91}} />
        <h4 className="message title">This screen is too small!</h4>
        <h5 className="message subtitle">Please grab a tablet or something larger.</h5>
      </div>

      <Board
        columns={columns}
        columnOrder={columnOrder}
        handleOnChangeSound={handleOnChangeSound}
        handleUpdateColumnOrder={handleUpdateColumnOrder}
        handleUpdateColumns={handleUpdateColumns}
        handleDeleteWord={handleDeleteWord}
        withScrollableColumns
        isEditable={isEditable}
      />

      {isCardVisible && <Card onClose={handleClickCard} />}

      <Drawer anchor="right" variant="persistent" open={isDrawerOpen} onClose={handleClickCloseDrawer}>
        <ProductsOverlay onClickActionBtn={handleClickAddItem} onClickClose={handleClickCloseDrawer} />
      </Drawer>
    </>
  );
}
